import { whenever } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import appConfig from '@/config';
import {
  useBillingStore,
  useDataSharesStore,
  useFeedsStore,
  useOauthApplicationStore,
  usePartnerCloudStore,
  usePartnersStore,
  usePopulationsStore,
} from '@/stores';
import {
  CUSTOMERS_STANDARD_TYPE,
  OPEN_OPPS_STANDARD_TYPE,
  OTHER,
  PROSPECTS_STANDARD_TYPE,
} from '@/types/populations';

import useAuth from './useAuth';
import useIntegrations from './useIntegrations';

export function useAppcues() {
  const { authorizations, currentOrg, currentUser } = useAuth();
  const {
    ready: partnersReady,
    error: partnersError,
    partnerOrgs,
    offlinePartners,
    pendingSentProposals,
    proposalsReceived,
  } = storeToRefs(usePartnersStore());
  const populationsStore = usePopulationsStore();
  const {
    ready: populationsReady,
    populations,
    error: populationsError,
  } = storeToRefs(populationsStore);
  const { isOnFreeTrial } = storeToRefs(useBillingStore());
  const { ready: pcReady, error: pcError } = storeToRefs(
    usePartnerCloudStore(),
  );
  const { ready: oauthReady, error: oauthError } = storeToRefs(
    useOauthApplicationStore(),
  );
  const {
    ready: feedsReady,
    error: feedsError,
    feeds,
  } = storeToRefs(useFeedsStore());
  const {
    ready: dataSharesReady,
    error: dataSharesError,
    isSharingDataWithPartners,
  } = storeToRefs(useDataSharesStore());

  const { allEnabledIntegrations } = useIntegrations();

  const ready = computed(
    () =>
      (partnersReady.value || partnersError.value) &&
      (populationsReady.value || populationsError.value) &&
      (pcReady.value || pcError.value) &&
      (oauthReady.value || oauthError.value) &&
      (feedsReady.value || feedsError.value) &&
      (dataSharesReady.value || dataSharesError.value),
  );

  const canGroup = computed(
    () =>
      ready.value &&
      !!currentUser.value?.id &&
      !!currentOrg.value?.id &&
      !!window.Appcues &&
      appConfig.appcuesEnabled,
  );

  whenever(
    canGroup,
    () => {
      window.Appcues?.group(currentOrg.value.id.toString(), {
        name: currentOrg.value.name,
        website: currentOrg.value.domain ?? '',
        created_at: currentOrg.value.created_at,
        is_dormant: currentOrg.value?.is_dormant,
        is_discoverable: currentOrg.value?.is_discoverable,
        num_partners: partnerOrgs.value.length,
        num_offline_partners: offlinePartners.value.length,
        num_prospects: populationsStore.getPopulationsBySegment(
          PROSPECTS_STANDARD_TYPE,
        ).length,
        num_open_opps: populationsStore.getPopulationsBySegment(
          OPEN_OPPS_STANDARD_TYPE,
        ).length,
        num_customers: populationsStore.getPopulationsBySegment(
          CUSTOMERS_STANDARD_TYPE,
        ).length,
        num_other: populationsStore.getPopulationsBySegment(OTHER).length,
        num_populations: populations.value.length,
        num_pending_sent_proposals: pendingSentProposals.value.length,
        num_pending_proposals_received: proposalsReceived.value.length,
        num_users: authorizations.value.length,
        is_on_free_trial: isOnFreeTrial.value ?? false,
        num_integrations: allEnabledIntegrations.value.length,
        data_sources: feeds.value
          .map((feed) => feed.integration.friendly_name)
          .join(', '),
        is_sharing_data_with_partners: isSharingDataWithPartners.value,
      });
    },
    { immediate: true, once: true },
  );
}
